/** Wix media variant scaling mode: `fit` letterboxes inside the box, `fill` covers it. */

const VARIANT_SUFFIX = /\/v[12]\/(fit|fill)\/[^/]+\/[^/]+$/;

/**
 * Rewrites a wixstatic media URL to a right-sized variant (`/v1/{fit|fill}/w_{w},h_{h},q_85/file.webp`)
 * so a 32px badge never downloads a full-size original.
 *
 * Returns the ORIGINAL string unchanged unless the URL is a clean (no query, no fragment) http(s)
 * `*.wixstatic.com` `/media/` URL and both dimensions are positive — a signed or tokened URL must never
 * be touched, because rewriting its path invalidates the signature. One existing trailing variant
 * segment is replaced when present, so re-scaling an already-scaled URL is idempotent rather than
 * additive.
 *
 * Pure, and shared 1:1 with iOS `String.wixScaledImageURL` (`DesignSystem/WixMediaURL.swift`) and
 * Android `wixScaledImageUrl` (`WixImageUrl.kt`) — same guards, same variant path, same `q_85`/webp.
 * Change one and change all three.
 */
export function wixScaledImageUrl(url, widthPx, heightPx, scaling) {
  if (!url || widthPx <= 0 || heightPx <= 0) {
    return url;
  }
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const scheme = parsed.protocol.toLowerCase();
  if (scheme !== 'http:' && scheme !== 'https:') {
    return url;
  }
  const host = parsed.hostname.toLowerCase();
  if (host !== 'static.wixstatic.com' && !host.endsWith('.wixstatic.com')) {
    return url;
  }
  if (!parsed.pathname.includes('/media/')) {
    return url;
  }
  // `search`/`hash` carry the signature on a tokened URL — leave those completely alone.
  if (parsed.search || parsed.hash) {
    return url;
  }
  const basePath = parsed.pathname.replace(VARIANT_SUFFIX, '').replace(/\/+$/, '');
  const width = Math.round(widthPx);
  const height = Math.round(heightPx);
  return `${parsed.protocol}//${parsed.host}${basePath}/v1/${scaling}/w_${width},h_${height},q_85/file.webp`;
}

/**
 * The pixel dimensions to request for a CSS-pixel box, at the display's density — capped at 3×, the
 * largest density worth downloading for (the same cap both native platforms apply).
 */
export function wixImagePixelSize(cssPx, devicePixelRatio) {
  return Math.round(cssPx * Math.min(Math.max(devicePixelRatio, 1), 3));
}
//# sourceMappingURL=wixImageUrl.js.map